import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Calendar, User, Mail, Phone, Euro, Building2, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { apartmentService, bookingService } from '../../services/api';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

// Enregistrer la locale française
registerLocale('fr', fr);

interface Apartment {
  id: number;
  titre: string;
  adresse: string;
  ville: string;
  prix_par_nuit: number;
}

// Les dates indisponibles sont des chaînes au format YYYY-MM-DD

const CreateInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ booking: any; sent: boolean } | null>(null);
  
  // Calendrier de disponibilité - liste de dates indisponibles (format YYYY-MM-DD)
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Dates sélectionnées
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [formData, setFormData] = useState({
    apartment_id: '',
    nb_personnes: 1,
    prix_total: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    notes: ''
  });

  useEffect(() => {
    const fetchApartments = async () => {
      try {
        const response = await apartmentService.getMyApartments();
        if (response.data?.apartments) {
          setApartments(response.data.apartments);
        }
      } catch (err: any) {
        console.error('Erreur chargement appartements:', err);
        setError('Erreur lors du chargement des appartements');
      } finally {
        setLoading(false);
      }
    };

    fetchApartments();
  }, []);

  // Charger les disponibilités quand un appartement est sélectionné
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!formData.apartment_id) {
        setUnavailableDates([]);
        return;
      }
      
      setLoadingAvailability(true);
      try {
        const response = await apartmentService.getAvailability(parseInt(formData.apartment_id));
        if (response.data?.unavailable_dates) {
          setUnavailableDates(response.data.unavailable_dates);
        }
      } catch (err: any) {
        console.error('Erreur chargement disponibilités:', err);
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, [formData.apartment_id]);

  // Vérifier si une date est occupée
  const isDateBooked = (date: Date): boolean => {
    // Utiliser la date locale (pas UTC) pour éviter les décalages de fuseau horaire
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return unavailableDates.includes(dateStr);
  };

  // Style personnalisé pour les jours du calendrier
  const getDayClassName = (date: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate < today) {
      return 'day-past';
    }
    
    if (isDateBooked(date)) {
      return 'day-booked';
    }
    
    return 'day-available';
  };

  const calculateNights = () => {
    if (startDate && endDate) {
      const diff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    }
    return 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!startDate || !endDate) {
      setError('Veuillez sélectionner les dates d\'arrivée et de départ');
      return;
    }

    // Validation: date_fin >= date_debut
    if (endDate < startDate) {
      setError('La date de fin ne peut pas être antérieure à la date de début');
      return;
    }
    
    setSubmitting(true);

    try {
      // Formater les dates en local (pas UTC) pour éviter les décalages de fuseau horaire
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const response = await bookingService.createInvitation({
        apartment_id: parseInt(formData.apartment_id),
        date_debut: formatLocalDate(startDate),
        date_fin: formatLocalDate(endDate),
        nb_personnes: formData.nb_personnes,
        prix_total: parseFloat(formData.prix_total),
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        notes: formData.notes || undefined
      });

      if (response.data?.success) {
        setSuccess({ booking: response.data.booking, sent: false });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la création de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!success?.booking?.id) return;
    setSending(true);
    setError(null);

    try {
      await bookingService.sendInvitation(success.booking.id);
      setSuccess(prev => prev ? { ...prev, sent: true } : null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Réservation créée avec succès !</h2>
          <p className="text-gray-600 mb-4">
            Code: <strong>{success.booking.code_reservation}</strong>
          </p>

          {success.sent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-700">
                ✅ L'invitation a été envoyée à <strong>{formData.guest_email}</strong>
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-700 mb-4">
                La réservation est prête. Envoyez l'invitation au client pour qu'il puisse payer et confirmer.
              </p>
              <button
                onClick={handleSendInvitation}
                disabled={sending}
                className="btn btn-primary flex items-center gap-2 mx-auto"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer l'invitation par email
                  </>
                )}
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline"
            >
              Retour
            </button>
            <button
              onClick={() => {
                setSuccess(null);
                setStartDate(null);
                setEndDate(null);
                setFormData({
                  apartment_id: '',
                  nb_personnes: 1,
                  prix_total: '',
                  guest_name: '',
                  guest_email: '',
                  guest_phone: '',
                  notes: ''
                });
              }}
              className="btn btn-primary"
            >
              Créer une autre réservation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* CSS personnalisé pour le datepicker */}
      <style>{`
        .day-available {
          background-color: #dcfce7 !important;
          color: #166534 !important;
        }
        .day-available:hover {
          background-color: #bbf7d0 !important;
        }
        .day-booked {
          background-color: #fee2e2 !important;
          color: #991b1b !important;
          pointer-events: none;
          opacity: 0.7;
        }
        .day-past {
          background-color: #f3f4f6 !important;
          color: #9ca3af !important;
        }
        .react-datepicker {
          font-family: inherit;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .react-datepicker__header {
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          padding-top: 12px;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__day--in-range {
          background-color: #3b82f6 !important;
          color: white !important;
        }
        .react-datepicker__day--keyboard-selected {
          background-color: #93c5fd !important;
        }
        .react-datepicker__input-container input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 1rem;
        }
        .react-datepicker__input-container input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
      `}</style>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Créer une réservation invitée</h1>
        <p className="text-gray-600">Créez une réservation et envoyez une invitation au client pour qu'il confirme et paie.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Appartement */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary-500" />
            Appartement
          </h2>
          <select
            name="apartment_id"
            value={formData.apartment_id}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg p-3"
          >
            <option value="">Sélectionner un appartement</option>
            {apartments.map(apt => (
              <option key={apt.id} value={apt.id}>
                {apt.titre} - {apt.ville} ({apt.prix_par_nuit.toLocaleString()} FCFA/nuit)
              </option>
            ))}
          </select>
        </div>

        {/* Dates et personnes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-500" />
            Dates et voyageurs
          </h2>
          
          {loadingAvailability && (
            <div className="flex items-center gap-2 mb-4 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Chargement des disponibilités...</span>
            </div>
          )}
          
          {formData.apartment_id && !loadingAvailability && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span className="text-gray-600">Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                  <span className="text-gray-600">Occupé</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date d'arrivée</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                minDate={new Date()}
                locale="fr"
                dateFormat="dd/MM/yyyy"
                placeholderText="Sélectionner..."
                dayClassName={formData.apartment_id ? getDayClassName : undefined}
                disabled={!formData.apartment_id}
                className="w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de départ</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate || new Date()}
                locale="fr"
                dateFormat="dd/MM/yyyy"
                placeholderText="Sélectionner..."
                dayClassName={formData.apartment_id ? getDayClassName : undefined}
                disabled={!formData.apartment_id || !startDate}
                className="w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de personnes</label>
              <input
                type="number"
                name="nb_personnes"
                value={formData.nb_personnes}
                onChange={handleChange}
                min={1}
                max={20}
                required
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            </div>
          </div>
          {calculateNights() > 0 && (
            <p className="mt-3 text-sm text-gray-600">
              Durée: <strong>{calculateNights()} nuit(s)</strong>
            </p>
          )}
          
          {!formData.apartment_id && (
            <p className="mt-3 text-sm text-amber-600">
              ⚠️ Sélectionnez d'abord un appartement pour voir les disponibilités
            </p>
          )}
        </div>

        {/* Prix */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Euro className="w-5 h-5 text-primary-500" />
            Prix personnalisé
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prix total (FCFA)</label>
            <input
              type="number"
              name="prix_total"
              value={formData.prix_total}
              onChange={handleChange}
              min={0}
              step={1000}
              required
              placeholder="Ex: 150000"
              className="w-full border border-gray-300 rounded-lg p-3"
            />
            <p className="mt-1 text-sm text-gray-500">
              Définissez le prix total que le client devra payer.
            </p>
          </div>
        </div>

        {/* Informations client */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-primary-500" />
            Informations du client
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
              <input
                type="text"
                name="guest_name"
                value={formData.guest_name}
                onChange={handleChange}
                required
                placeholder="Jean Dupont"
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4" /> Email
              </label>
              <input
                type="email"
                name="guest_email"
                value={formData.guest_email}
                onChange={handleChange}
                required
                placeholder="jean@example.com"
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone className="w-4 h-4" /> Téléphone
              </label>
              <input
                type="tel"
                name="guest_phone"
                value={formData.guest_phone}
                onChange={handleChange}
                required
                placeholder="+221 77 123 45 67"
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Notes (optionnel)</h2>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            placeholder="Notes internes sur cette réservation..."
            className="w-full border border-gray-300 rounded-lg p-3"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-outline"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Création en cours...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Créer la réservation
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateInvitationPage;
